import { FC } from "react";

import { FormBackgroundTheme, parseFormBackgroundTheme, resolveBackgroundThemeCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorStepColorStateless, StepColorFormData } from "./LiveFormEditorStepColor.stateless";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getColorChangedEventProperties } from "@/components/LiveEditorElements/liveEditorColors";

export const TicketingLiveEditorStepColor: FC = () => {
  const {
    apiFormObject: apiTicketing,
    featureFlags: {
      isDelightfulPagesActive,
      isDelightfulPagesLoading,
      isCustomColorTileActive,
      isCustomColorTileLoading,
    },
  } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { t } = useTranslate();

  const currentBackgroundTheme = parseFormBackgroundTheme(apiTicketing.backgroundTheme);

  const defaultValues: StepColorFormData = {
    color: apiTicketing.color,
    isDarkMode: Boolean(apiTicketing.isDarkMode),
    backgroundTheme: currentBackgroundTheme,
    colorToShowInPreview: apiTicketing.color,
  };
  const form = useForm<StepColorFormData>({ defaultValues });

  const updateTicketingVisualOptions = trpc.updateTicketingVisualOptions.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepColorFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      return await updateTicketingVisualOptions.mutateAsync({
        ticketingId: apiTicketing.id,
        color: formData.color,
        isDarkMode: formData.isDarkMode,
        backgroundTheme: formData.backgroundTheme,
      });
    },
    errorMessage: t("dashboard", "liveFormEditor.stepColor.error"),
    getSubmitTrackingProperties: (formData) => ({
      ...getColorChangedEventProperties({
        color: formData.color,
        colorOnStepArrival: defaultValues.color,
        editorType: "ticketing",
      }),
      is_dark_mode: formData.isDarkMode,
      background_theme: formData.backgroundTheme,
      background_category: resolveBackgroundThemeCategory(formData.backgroundTheme),
      is_delightful_pages_active: isDelightfulPagesActive,
    }),
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.color = formData.colorToShowInPreview;
      liveDataToMutate.ticketing.isDarkMode = formData.isDarkMode;
      liveDataToMutate.ticketing.backgroundTheme = formData.backgroundTheme;
    },
  });

  const handleTrackOnColorChange = (color: string) =>
    logUserActionEvent(
      AmplitudeEvents.FormColorChanged,
      getColorChangedEventProperties({ color, colorOnStepArrival: defaultValues.color, editorType: "ticketing" })
    );

  const handleTrackOnCustomColorCancel = () =>
    logUserActionEvent(AmplitudeEvents.FormCustomColorCancelled, { editor_type: "ticketing" });

  const handleTrackOnBackgroundStyleChange = (theme: FormBackgroundTheme | null, category: string) =>
    logUserActionEvent(AmplitudeEvents.FormBackgroundStyleChanged, {
      background_theme: theme,
      background_category: category,
    });

  const handleTrackOnDarkModeChange = (isDarkMode: boolean) =>
    logUserActionEvent(AmplitudeEvents.FormDarkModeChanged, { is_dark_mode: isDarkMode });

  return (
    <LiveFormEditorStepColorStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      trackOnColorChange={handleTrackOnColorChange}
      trackOnCustomColorCancel={handleTrackOnCustomColorCancel}
      trackOnBackgroundStyleChange={handleTrackOnBackgroundStyleChange}
      trackOnDarkModeChange={handleTrackOnDarkModeChange}
      isDelightfulPagesActive={isDelightfulPagesActive}
      isDelightfulPagesLoading={isDelightfulPagesLoading}
      isCustomColorTileActive={isCustomColorTileActive}
      isCustomColorTileLoading={isCustomColorTileLoading}
    />
  );
};
